import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { inclusionTravelPeriodFilter } from './inclusionsTravelPeriodFilter'
import luxPlusInclusionFilter from './luxPlusInclusionFilter'
import { partitionBy } from 'lib/array/arrayUtils'
import { inclusionIconMap } from 'constants/inclusions'

interface GetInclusionsOptions {
  checkIn?: moment.Moment | string
  checkOut?: moment.Moment | string
  luxPlusTier?: App.MembershipTier
}

export function extractPkgInclusions(pkg: App.Package | undefined, options: GetInclusionsOptions = {}) {
  const { checkIn, checkOut, luxPlusTier } = options

  if (!pkg) {
    return [[], [], [], []]
  }

  const isoCheckIn = typeof checkIn === 'string' ? checkIn : checkIn?.format(ISO_DATE_FORMAT)
  const isoCheckOut = typeof checkOut === 'string' ? checkOut : checkOut?.format(ISO_DATE_FORMAT)
  const [bonusInclusionsRaw, inclusionsRaw] = partitionBy(pkg.inclusions ?? [], inclusion => !!inclusion?.isBonus)
  const inclusions = inclusionTravelPeriodFilter(inclusionsRaw, isoCheckIn, isoCheckOut)
  const bonusInclusions = inclusionTravelPeriodFilter([...bonusInclusionsRaw, ...pkg.bonusInclusions], isoCheckIn, isoCheckOut)
  const mainInclusions = [...inclusions, ...bonusInclusions]
  const luxPlusInclusions = luxPlusTier && pkg.luxPlusInclusionsByTier ? luxPlusInclusionFilter(pkg.luxPlusInclusionsByTier[luxPlusTier as App.MembershipSubscriptionTier] ?? [], mainInclusions) : []
  const tacticalInclusions = mainInclusions.filter(incl => incl.hasTactical)

  return [
    inclusions,
    bonusInclusions,
    luxPlusInclusions,
    tacticalInclusions,
  ]
}

export function getPkgInclusionsCount(pkg: App.Package, options: GetInclusionsOptions = {}) {
  const [inclusions, bonusInclusions, luxPlusInclusions] = extractPkgInclusions(pkg, options)

  return inclusions.length + bonusInclusions.length + luxPlusInclusions.length
}

/**
 * Reorders tile inclusions by boosting those whose symbol maps to an active inclusion filter.
 * Uses inclusionIconMap to translate symbol (icon name) to inclusion type string.
 * Non-matching inclusions maintain their original relative order.
 *
 * @param inclusions - Array of OfferInclusions to reorder (has symbol field)
 * @param activeInclusions - Set of inclusion type strings to boost (e.g., Set(['Breakfast', 'Spa treatment']))
 * @returns New array with matching inclusions boosted to the top
 */
export function reorderTileInclusionsByActiveFilters<T extends App.OfferInclusion>(
  inclusions: Array<T>,
  activeInclusions: Set<string>,
): Array<T> {
  if (!activeInclusions.size || !inclusions.length) {
    return inclusions
  }

  const boosted: Array<T> = []
  const rest: Array<T> = []
  for (const inclusion of inclusions) {
    const inclusionType = inclusion.symbol ? inclusionIconMap[inclusion.symbol] : undefined
    if (inclusionType && activeInclusions.has(inclusionType)) {
      boosted.push({ ...inclusion, isHighlighted: true })
    } else {
      rest.push({ ...inclusion, isHighlighted: false })
    }
  }

  return [...boosted, ...rest]
}
